<template>
  <div class="scl-page">
    <q-linear-progress v-if="this.isFetching" indeterminate color="primary" class="scl-page__progress" />

    <div class="scl-page__header">
      <PageTitle>Журнал изменений сметы</PageTitle>
      <q-btn flat round icon="refresh" color="primary" :loading="this.isFetching" @click="this.loadRows()" />
    </div>

    <!-- ===== Фильтры ===== -->
    <div class="scl-filters">
      <q-input
        :model-value="this.projectLabel"
        readonly outlined dense placeholder="Проект УСП"
        class="scl-filters__item scl-filters__item--wide"
        clearable
        @clear="this.clearProject">
        <template #prepend>
          <q-btn icon="list" flat dense round @click="this.openUspDialog">
            <q-tooltip>Выбрать проект УСП</q-tooltip>
          </q-btn>
        </template>
      </q-input>

      <div class="scl-filters__item">
        <CustomSelect
          :options="this.entrancesOptions"
          label="Подъезд"
          option-label-key="block_label"
          option-value-key="project_entrance_id"
          :value="this.filters.project_entrance_id"
          :disable="!this.hasScopeProject"
          :loading="this.entrancesLoading"
          @change="(v) => this.onFilter('project_entrance_id', v)"
        />
      </div>

      <div class="scl-filters__item scl-filters__item--narrow">
        <CustomSelect
          :options="this.floorsOptions"
          label="Этаж"
          option-label-key="level_label"
          option-value-key="floor_map_id"
          :value="this.filters.floor_map_id"
          :disable="!this.hasScopeProject"
          :loading="this.floorsLoading"
          @change="(v) => this.onFilter('floor_map_id', v)"
        />
      </div>

      <div class="scl-filters__item">
        <CustomSelect
          :options="this.checkGroupsOptions"
          label="Группа работ"
          option-label-key="work_set_check_group_name"
          option-value-key="work_set_check_group_id"
          :value="this.filters.work_set_check_group_id"
          :disable="!this.hasScopeProject"
          :loading="this.checkGroupsLoading"
          @change="(v) => this.onFilter('work_set_check_group_id', v)"
        />
      </div>

      <div class="scl-filters__item scl-filters__item--narrow">
        <div class="custom-select__label">project_id</div>
        <q-input
          :model-value="this.projectIdInput"
          outlined dense clearable
          type="number"
          placeholder="Поиск по project_id"
          hide-bottom-space
          @update:model-value="this.onProjectIdInput"
          @clear="this.onProjectIdInput(null)"
        />
      </div>

      <q-btn
        flat dense icon="filter_alt_off" color="grey-7" label="Сбросить"
        class="scl-filters__btn"
        @click="this.resetFilters"
      />

      <q-space />

      <div class="scl-filters__total">
        Всего записей: <b>{{ this.totalCount }}</b>
      </div>
    </div>

    <!-- ===== Панель массовых действий ===== -->
    <div class="scl-toolbar">
      <span v-if="this.selectedIds.length > 0" class="scl-count text-primary">
        Выбрано: {{ this.selectedIds.length }}
      </span>
      <q-space />
      <q-btn
        v-if="this.selectedIds.length > 0"
        unelevated size="sm" color="negative" icon="delete"
        :label="'Удалить выбранные (' + this.selectedIds.length + ')'"
        :loading="this.bulkDeleting"
        @click="this.onDeleteSelected"
      />
    </div>

    <!-- ===== Таблица ===== -->
    <q-table
      :rows="this.rowsData"
      :columns="this.columns"
      :loading="this.isFetching"
      row-key="smeta_change_log_id"
      flat dense wrap-cells
      :rows-per-page-options="[0]"
      no-data-label="Нет данных">

      <template #header="props">
        <q-tr :props="props">
          <q-th auto-width style="width:40px; padding:0 4px;">
            <q-checkbox
              :model-value="this.allSelected"
              :indeterminate="this.someSelected"
              :disable="this.rowsData.length === 0"
              @update:model-value="this.toggleSelectAll"
            />
          </q-th>
          <q-th v-for="col in props.cols" :key="col.name" :props="props">
            {{ col.label }}
          </q-th>
        </q-tr>
      </template>

      <template #body="props">
        <q-tr :props="props">
          <q-td auto-width style="width:40px; padding:0 4px;">
            <q-checkbox
              :model-value="this.selectedIds.includes(props.row.smeta_change_log_id)"
              @update:model-value="(v) => this.toggleSelectItem(props.row.smeta_change_log_id, v)"
            />
          </q-td>
          <q-td v-for="col in props.cols" :key="col.name" :props="props">
            <template v-if="col.name === 'project_info'">
              <div class="scl-cell">
                <span class="scl-project-name">{{ props.row.project_name || '—' }}</span>
                <span v-if="props.row.project_id" class="scl-pid"
                      :class="{ 'scl-pid--active': filters.project_id === props.row.project_id }"
                      @click="setProjectIdFilter(props.row.project_id)">
                  project_id: <b>{{ props.row.project_id }}</b>
                  <q-tooltip>Фильтровать по project_id {{ props.row.project_id }}</q-tooltip>
                </span>
                <span v-if="props.row.sbs_project_id" class="scl-muted">
                  sbs_project_id: {{ props.row.sbs_project_id }}
                </span>
              </div>
            </template>
            <template v-else-if="col.name === 'location'">
              <div class="scl-cell">
                <span v-if="props.row.block_name || props.row.entrance" class="scl-location">
                  <q-icon name="door_front" size="14px" class="q-mr-xs" />
                  {{ props.row.block_name || ('Подъезд ' + props.row.entrance) }}
                </span>
                <span v-if="props.row.level_num !== null && props.row.level_num !== undefined" class="scl-location">
                  <q-icon name="layers" size="14px" class="q-mr-xs" />
                  Этаж {{ props.row.level_num }}
                </span>
              </div>
            </template>
            <template v-else-if="col.name === 'check_group'">
              <div class="scl-cell">
                <span v-if="props.row.work_set_check_group_name" class="scl-cg-name">
                  {{ props.row.work_set_check_group_name }}
                </span>
                <span v-else class="scl-muted">—</span>
              </div>
            </template>
            <template v-else-if="col.name === 'work_info'">
              <div class="scl-cell">
                <span class="scl-work-name">{{ props.row.work_set_name || '—' }}</span>
                <span v-if="props.row.work_guid" class="scl-muted scl-guid">{{ props.row.work_guid }}</span>
              </div>
            </template>
            <template v-else-if="col.name === 'status_code'">
              <q-badge
                :color="statusColor(props.row.status_code)"
                :label="statusLabel(props.row.status_code)"
                class="scl-status-badge"
              />
            </template>
            <template v-else-if="col.name === 'agreement'">
              <q-badge
                v-if="props.row.contractor_agreement_list_id"
                color="teal-7"
                class="scl-agreement-badge"
                :label="'ДС #' + props.row.contractor_agreement_list_id"
              >
                <q-tooltip>По этой записи сформирован ДС (contractor_agreement_list_id = {{ props.row.contractor_agreement_list_id }})</q-tooltip>
              </q-badge>
              <span v-else class="scl-muted">—</span>
            </template>
            <template v-else-if="col.name === 'price'">
              <div class="scl-diff">
                <span class="scl-old">{{ formatNum(props.row.old_price) }}</span>
                <q-icon name="arrow_forward" size="12px" class="scl-arrow" />
                <span class="scl-new">{{ formatNum(props.row.new_price) }}</span>
              </div>
            </template>
            <template v-else-if="col.name === 'amount'">
              <div class="scl-diff">
                <span class="scl-old">{{ formatNum(props.row.old_amount) }}</span>
                <q-icon name="arrow_forward" size="12px" class="scl-arrow" />
                <span class="scl-new">{{ formatNum(props.row.new_amount) }}</span>
              </div>
            </template>
            <template v-else-if="col.name === 'total_sum'">
              <div class="scl-diff scl-diff--sum">
                <span class="scl-old">{{ formatNum(props.row.old_total_sum) }}</span>
                <q-icon name="arrow_forward" size="12px" class="scl-arrow" />
                <span class="scl-new">{{ formatNum(props.row.new_total_sum) }}</span>
              </div>
            </template>
            <template v-else-if="col.name === 'rowversion'">
              <span class="scl-muted">{{ formatDateTime(props.row.rowversion) }}</span>
            </template>
            <template v-else-if="col.name === 'actions'">
              <q-btn
                round flat dense size="sm" color="negative" icon="delete"
                :loading="!!deleting[props.row.smeta_change_log_id]"
                @click="onDeleteRow(props.row)">
                <q-tooltip>Удалить запись</q-tooltip>
              </q-btn>
            </template>
            <template v-else>{{ col.value }}</template>
          </q-td>
        </q-tr>
      </template>

    </q-table>

    <div v-if="this.pages > 1" class="scl-pagination">
      <q-pagination
        :model-value="this.page"
        :max="this.pages"
        :max-pages="7"
        color="grey-8"
        size="sm"
        boundary-numbers
        direction-links
        @update:model-value="this.onPageChange"
      />
    </div>

    <!-- ===== Диалог выбора проекта УСП ===== -->
    <q-dialog v-model="this.uspDialog.show" persistent>
      <q-card style="min-width: 800px; max-width: 1000px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Выбор проекта УСП</div>
          <q-space />
          <q-btn icon="close" flat round dense @click="this.uspDialog.show = false" />
        </q-card-section>

        <q-card-section>
          <CustomInput
            :value="this.uspDialog.search"
            :isSearch="true"
            placeholder="Поиск по названию проекта или типу..."
            v-debounce:300="(v) => this.uspDialog.search = v || ''"
          />

          <div style="margin-top: 12px;">
            <q-linear-progress v-if="this.uspDialog.loading" indeterminate />

            <div v-else-if="this.uspFilteredRows.length === 0"
              style="text-align:center;padding:40px;color:#999;">
              <q-icon name="folder_off" size="48px" />
              <div style="margin-top:12px;">Проекты не найдены</div>
            </div>

            <q-table
              v-else
              :rows="this.uspFilteredRows"
              :columns="this.uspColumns"
              row-key="project_group_id"
              flat bordered
              :rows-per-page-options="[10, 20, 50]"
              :pagination="{ rowsPerPage: 10 }">
              <template #body-cell-actions="props">
                <q-td :props="props">
                  <q-btn label="Выбрать" color="primary" size="sm" unelevated
                    @click="this.onUspProjectSelect(props.row)" />
                </q-td>
              </template>
            </q-table>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="this.uspDialog.show = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import PageTitle from "@/components/common/PageTitle";
import CustomSelect from "@/components/form/CustomSelect";
import CustomInput from "@/components/form/CustomInput";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useQuasar } from "quasar";

import { smetaChangeLogAPI } from "./services/api";
import {
  PAGE_SIZE,
  smetaChangeLogColumns,
  STATUS_LABELS,
  STATUS_COLORS,
  formatNum,
  formatDateTime,
} from "./services";

export default {
  name: "SmetaChangeLog",

  components: { PageTitle, CustomSelect, CustomInput },

  data() {
    return {
      columns: smetaChangeLogColumns,

      rowsData: [],
      isFetching: false,

      page:        1,
      pages:       1,
      totalCount:  0,

      filters: {
        sbs_project_id:          null,
        project_entrance_id:     null,
        floor_map_id:            null,
        work_set_check_group_id: null,
        project_id:              null,
      },
      projectLabel:   null,
      projectIdInput: "",
      projectIdDebounceTimer: null,

      entrancesOptions:    [],
      floorsOptions:       [],
      checkGroupsOptions:  [],
      entrancesLoading:    false,
      floorsLoading:       false,
      checkGroupsLoading:  false,

      deleting: {},
      selectedIds:  [],
      bulkDeleting: false,

      uspDialog: {
        show:    false,
        loading: false,
        search:  "",
      },
      uspAllProjects: [],
      uspColumns: [
        { name: "project_name", label: "Название проекта", field: "project_name", align: "left", sortable: true },
        { name: "project_type", label: "Тип проекта",      field: "project_type", align: "left", sortable: true },
        { name: "actions",      label: "Действия",         field: "actions",      align: "center" },
      ],
    };
  },

  computed: {
    hasScopeProject() {
      return !!this.filters.sbs_project_id || !!this.filters.project_id;
    },
    pageRowIds() {
      return this.rowsData.map((r) => r.smeta_change_log_id);
    },
    allSelected() {
      if (this.pageRowIds.length === 0) return false;
      return this.pageRowIds.every((id) => this.selectedIds.includes(id));
    },
    someSelected() {
      if (this.pageRowIds.length === 0) return false;
      const cnt = this.pageRowIds.filter((id) => this.selectedIds.includes(id)).length;
      return cnt > 0 && cnt < this.pageRowIds.length;
    },

    uspFilteredRows() {
      if (!this.uspDialog.search) return this.uspAllProjects;
      const q = this.uspDialog.search.toLowerCase();
      return this.uspAllProjects.filter(
        (p) =>
          (p.project_name || "").toLowerCase().includes(q) ||
          (p.project_type || "").toLowerCase().includes(q)
      );
    },
  },

  methods: {
    formatNum,
    formatDateTime,

    statusLabel(code) { return STATUS_LABELS[code] || code || "—"; },
    statusColor(code) { return STATUS_COLORS[code] || "grey-6"; },

    async loadRows(page) {
      if (typeof page === 'number' && Number.isFinite(page)) {
        this.page = page;
      }
      this.isFetching = true;
      try {
        const res = await smetaChangeLogAPI.getRows({
          sbs_project_id:          this.filters.sbs_project_id          ?? undefined,
          project_entrance_id:     this.filters.project_entrance_id     ?? undefined,
          floor_map_id:            this.filters.floor_map_id            ?? undefined,
          work_set_check_group_id: this.filters.work_set_check_group_id ?? undefined,
          project_id:              this.filters.project_id              ?? undefined,
          page:  this.page,
          limit: PAGE_SIZE,
        });
        if (!res) {
          this.rowsData   = [];
          this.totalCount = 0;
          this.pages      = 1;
          return;
        }
        this.rowsData   = res?.data || [];
        this.totalCount = parseInt(res?.total_count) || 0;
        this.pages      = Math.ceil(this.totalCount / PAGE_SIZE) || 1;
      } catch (e) {
        this.rowsData   = [];
        this.totalCount = 0;
        this.pages      = 1;
        const msg = e?.response?.data?.error || e?.message || 'Не удалось загрузить журнал изменений';
        this.showErrorSnackbar(msg);
      } finally {
        this.isFetching = false;
      }
    },

    onPageChange(page) {
      this.loadRows(page);
    },

    async loadEntrances() {
      if (!this.hasScopeProject) {
        this.entrancesOptions = [];
        return;
      }
      this.entrancesLoading = true;
      try {
        const res = await smetaChangeLogAPI.getEntrances(
          this.filters.sbs_project_id ?? undefined,
          this.filters.project_id     ?? undefined,
        );
        this.entrancesOptions = res?.data || [];
      } catch (e) {
        this.entrancesOptions = [];
        const msg = e?.response?.data?.error || e?.message || 'Не удалось загрузить подъезды';
        this.showErrorSnackbar(msg);
      } finally {
        this.entrancesLoading = false;
      }
    },

    async loadFloors() {
      if (!this.hasScopeProject) {
        this.floorsOptions = [];
        return;
      }
      this.floorsLoading = true;
      try {
        const res = await smetaChangeLogAPI.getFloors(
          this.filters.sbs_project_id      ?? undefined,
          this.filters.project_entrance_id ?? undefined,
          this.filters.project_id          ?? undefined,
        );
        const data = res?.data || [];
        this.floorsOptions = data.map((r) => ({
          ...r,
          level_label: r.level_num !== null && r.level_num !== undefined ? String(r.level_num) : "—",
        }));
      } catch (e) {
        this.floorsOptions = [];
        const msg = e?.response?.data?.error || e?.message || 'Не удалось загрузить этажи';
        this.showErrorSnackbar(msg);
      } finally {
        this.floorsLoading = false;
      }
    },

    async loadCheckGroups() {
      if (!this.hasScopeProject) {
        this.checkGroupsOptions = [];
        return;
      }
      this.checkGroupsLoading = true;
      try {
        const res = await smetaChangeLogAPI.getCheckGroups(
          this.filters.sbs_project_id      ?? undefined,
          this.filters.project_entrance_id ?? undefined,
          this.filters.floor_map_id        ?? undefined,
          this.filters.project_id          ?? undefined,
        );
        this.checkGroupsOptions = res?.data || [];
      } catch (e) {
        this.checkGroupsOptions = [];
        const msg = e?.response?.data?.error || e?.message || 'Не удалось загрузить группы работ';
        this.showErrorSnackbar(msg);
      } finally {
        this.checkGroupsLoading = false;
      }
    },

    onFilter(key, value) {
      this.filters[key] = value ?? null;
      if (key === "project_entrance_id") {
        this.filters.floor_map_id            = null;
        this.filters.work_set_check_group_id = null;
        this.loadFloors();
        this.loadCheckGroups();
      } else if (key === "floor_map_id") {
        this.filters.work_set_check_group_id = null;
        this.loadCheckGroups();
      }
      this.loadRows(1);
    },

    resetFilters() {
      this.filters = {
        sbs_project_id:          null,
        project_entrance_id:     null,
        floor_map_id:            null,
        work_set_check_group_id: null,
        project_id:              null,
      };
      this.projectLabel        = null;
      this.projectIdInput      = "";
      this.entrancesOptions    = [];
      this.floorsOptions       = [];
      this.checkGroupsOptions  = [];
      this.loadRows(1);
    },

    clearProject() {
      this.filters.sbs_project_id          = null;
      this.filters.project_entrance_id     = null;
      this.filters.floor_map_id            = null;
      this.filters.work_set_check_group_id = null;
      this.projectLabel        = null;
      this.entrancesOptions    = [];
      this.floorsOptions       = [];
      this.checkGroupsOptions  = [];
      this.loadRows(1);
    },

    onProjectIdInput(value) {
      this.projectIdInput = value ?? "";
      if (this.projectIdDebounceTimer) clearTimeout(this.projectIdDebounceTimer);
      this.projectIdDebounceTimer = setTimeout(() => {
        const num = this.projectIdInput === "" || this.projectIdInput === null
          ? null
          : parseInt(this.projectIdInput);
        const next = Number.isNaN(num) ? null : num;
        this.applyProjectIdFilter(next);
      }, 400);
    },

    setProjectIdFilter(project_id) {
      if (!project_id) return;
      if (this.filters.project_id === project_id) return;
      this.projectIdInput = String(project_id);
      this.applyProjectIdFilter(project_id);
    },

    applyProjectIdFilter(next) {
      if (this.filters.project_id === next) return;
      this.filters.project_id = next;
      this.filters.project_entrance_id     = null;
      this.filters.floor_map_id            = null;
      this.filters.work_set_check_group_id = null;
      this.entrancesOptions    = [];
      this.floorsOptions       = [];
      this.checkGroupsOptions  = [];

      if (this.hasScopeProject) {
        this.loadEntrances();
        this.loadFloors();
        this.loadCheckGroups();
      }
      this.loadRows(1);
    },

    async openUspDialog() {
      this.uspDialog.search = "";
      this.uspDialog.show   = true;
      if (this.uspAllProjects.length > 0) return;

      this.uspDialog.loading = true;
      try {
        const res = await smetaChangeLogAPI.getUspProjects();
        this.uspAllProjects = res?.data || res || [];
      } catch (e) {
        this.uspAllProjects = [];
        const msg = e?.response?.data?.error || e?.message || 'Не удалось загрузить проекты УСП';
        this.showErrorSnackbar(msg);
      } finally {
        this.uspDialog.loading = false;
      }
    },

    onUspProjectSelect(row) {
      const label = row.project_name + (row.project_type ? ` (${row.project_type})` : "");
      this.filters.sbs_project_id          = row.sbs_project_id ?? null;
      this.filters.project_entrance_id     = null;
      this.filters.floor_map_id            = null;
      this.filters.work_set_check_group_id = null;
      this.projectLabel        = label;
      this.entrancesOptions    = [];
      this.floorsOptions       = [];
      this.checkGroupsOptions  = [];
      this.uspDialog.show      = false;

      this.loadEntrances();
      this.loadCheckGroups();
      this.loadRows(1);
    },

    onDeleteRow(row) {
      const id = row.smeta_change_log_id;
      this.$q.dialog({
        title: "Подтверждение",
        message: `Удалить запись журнала изменений #${id}?`,
        cancel: true,
        persistent: true,
        ok: { label: "Удалить", color: "negative", unelevated: true },
      }).onOk(async () => {
        this.deleting = { ...this.deleting, [id]: true };
        const res = await smetaChangeLogAPI.deleteRows([id]);
        this.deleting = { ...this.deleting, [id]: false };
        if (!res) return;
        this.selectedIds = this.selectedIds.filter((x) => x !== id);
        this.showSucceedSnackbar("Запись удалена");
        await this.loadRows();
      });
    },

    toggleSelectAll(value) {
      if (value) {
        const ids = [...new Set([...this.selectedIds, ...this.pageRowIds])];
        this.selectedIds = ids;
      } else {
        this.selectedIds = this.selectedIds.filter(
          (id) => !this.pageRowIds.includes(id),
        );
      }
    },

    toggleSelectItem(id, value) {
      if (value) {
        if (!this.selectedIds.includes(id)) this.selectedIds.push(id);
      } else {
        this.selectedIds = this.selectedIds.filter((x) => x !== id);
      }
    },

    onDeleteSelected() {
      const ids = [...this.selectedIds];
      if (ids.length === 0) return;
      this.$q.dialog({
        title: "Подтверждение",
        message: `Удалить выбранные записи (${ids.length} шт.)?`,
        cancel: true,
        persistent: true,
        ok: { label: "Удалить", color: "negative", unelevated: true },
      }).onOk(async () => {
        this.bulkDeleting = true;
        const res = await smetaChangeLogAPI.deleteRows(ids);
        this.bulkDeleting = false;
        if (!res) return;
        const deleted = res?.data?.deleted_count ?? ids.length;
        this.selectedIds = [];
        this.showSucceedSnackbar(`Удалено записей: ${deleted}`);
        await this.loadRows();
      });
    },
  },

  mounted() {
    this.loadRows();
  },

  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    const $q = useQuasar();
    return { showSucceedSnackbar, showErrorSnackbar, $q };
  },
};
</script>

<style scoped>
.scl-page {
  padding: 16px;
  position: relative;
}

.scl-page :deep(.q-table) {
  table-layout: auto;
  width: 100%;
}
.scl-page :deep(.q-table thead th) {
  background: #fafafa;
  font-weight: 600;
}

.scl-page__progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.scl-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.scl-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 0;
}
.scl-filters__item {
  min-width: 180px;
  max-width: 280px;
}
.scl-filters__item--wide {
  min-width: 260px;
  max-width: 380px;
}
.scl-filters__item--narrow {
  min-width: 120px;
  max-width: 150px;
}
.scl-filters__btn {
  margin-bottom: 2px;
}
.scl-filters__total {
  font-size: 13px;
  color: #555;
  padding-bottom: 4px;
}

/* ── Ячейки ── */
.scl-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.3;
}
.scl-project-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a2332;
}
.scl-location {
  font-size: 12px;
  color: #444;
  display: inline-flex;
  align-items: center;
}
.scl-muted {
  font-size: 11px;
  color: #999;
  font-family: monospace;
}
.scl-pid {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  font-size: 11px;
  font-family: monospace;
  color: #1a73e8;
  background: #e8f0fe;
  border-radius: 4px;
  padding: 2px 6px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  border: 1px solid transparent;
}
.scl-pid:hover {
  background: #d2e3fc;
}
.scl-pid b {
  font-weight: 700;
  font-size: 12px;
}
.scl-pid--active {
  background: #1a73e8;
  color: #fff;
  border-color: #1557b0;
}
.scl-pid--active b {
  color: #fff;
}
.scl-guid {
  word-break: break-all;
}

.scl-tag {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  background: #e8eaf6;
  color: #3949ab;
  border-radius: 3px;
  padding: 1px 5px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  align-self: flex-start;
}
.scl-cg-name {
  font-size: 12px;
  color: #333;
}
.scl-work-name {
  font-size: 12px;
  color: #1a2332;
  font-weight: 500;
}

.scl-status-badge {
  font-size: 11px !important;
  font-weight: 600 !important;
  padding: 3px 8px !important;
  border-radius: 10px !important;
}

.scl-agreement-badge {
  font-size: 11px !important;
  font-weight: 600 !important;
  padding: 3px 8px !important;
  border-radius: 10px !important;
}

/* ── Diff old → new ── */
.scl-diff {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  white-space: nowrap;
}
.scl-diff--sum {
  font-weight: 600;
}
.scl-old {
  color: #b0b0b0;
  text-decoration: line-through;
}
.scl-new {
  color: #2e7d32;
  font-weight: 600;
}
.scl-arrow {
  color: #bbb;
}

.scl-pagination {
  display: flex;
  justify-content: center;
  padding: 12px 0 4px;
}

.scl-toolbar {
  display: flex;
  align-items: center;
  padding: 4px 0 6px;
  min-height: 32px;
}
.scl-count {
  font-size: 13px;
  font-weight: 600;
}
</style>
